import React from 'react'
import styles from './FaceMatchVerificationDialog.module.css'
import NoImgPlaceholder from "../../../assets/images/no-image.png"

import {
    Dialog, DialogActions, DialogContent,
    DialogTitle, useMediaQuery, Button, IconButton, Tooltip
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/material/styles';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

import {
    TransformWrapper, TransformComponent
} from "react-zoom-pan-pinch";


const StyledDialog = styled(Dialog)(({ theme, dialogType }) => ({
    '& .MuiDialog-paper': {
        width: dialogType === 'secondaryDocument' ? '70vw' : '85vw',
        height: 'auto',
        maxHeight: '85vh',
        margin: '24px',
        borderRadius: '8px',
        overflow: 'hidden'
    },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
    // backgroundColor: theme.palette.primary.main,
    // color: theme.palette.primary.contrastText,
    padding: '16px 24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
    padding: '36px 24px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '600px', // Minimum height for consistency
    height: 'auto'
}));

const ImagesContainer = styled('div')(({ theme, dialogType, imgCount }) => ({
    display: 'flex',
    justifyContent: ((dialogType === 'faceMatch' && imgCount === 1) || dialogType === 'secondaryDocument') ? 'center' : 'space-between',
    gap: '16px',
    width: '100%',
    height: imgCount === 1 ? '70vh' : '68vh',
}));

const ImageWrapper = styled('div')(({ theme, dialogType }) => ({
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexGrow: 1,
    borderRadius: '8px',
    border: '2px solid #D0D0D0',
    overflow: 'hidden',
}));

const StyledImage = styled('img')(({ theme, imageType }) => ({
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '100%',
    height: 'auto',
    maxWidth: '100%',
    borderRadius: '4px',
    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
}));

const ZoomControlsContainer = styled('div')(({ theme }) => ({
    position: 'absolute',
    bottom: '20px',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    justifyContent: 'center',
    gap: '8px',
    padding: '4px 10px',
    backgroundColor: 'rgba(255, 255, 255)',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
    zIndex: 10,
}));


const ZoomDialog = ({ isOpen, onClose, title, dialogType, images }) => {

    const getImageType = () => {
        return dialogType === 'faceMatch' ? 'portrait' : 'document';
    };

    // Calculate width percentage based on number of images
    const getImageWidth = () => {
        const count = images.length;
        switch (count) {
            case 1:
                return '100%';
            case 2:
                return 'calc(50% - 8px)';  // 50% minus half the gap
            case 3:
                return 'calc(33.333% - 11px)'; // 33.333% minus gap adjustment
            default:
                return '100%';
        }
    };

    console.log("Images: ", images)

    return (
        <StyledDialog
            open={isOpen}
            onClose={onClose}
            fullWidth
            maxWidth={false}
            dialogType={dialogType}
        >
            <StyledDialogTitle>
                {title}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {/* Tooltip for the help/info button */}
                    <Tooltip
                        title="You can zoom in and out using your mouse scroll or pinch gesture. 
                        Click and drag to move around the zoomed image. 
                        Use the on-screen control buttons to zoom, reset, or fit the image to view. 
                        This helps you inspect every detail clearly."
                        arrow
                    >
                        <IconButton
                            aria-label="help"
                            sx={{ color: 'inherit' }}
                        >
                            <InfoOutlinedIcon />
                        </IconButton>
                    </Tooltip>

                    {/* Close button */}
                    <IconButton
                        aria-label="close"
                        onClick={onClose}
                        sx={{ color: 'inherit' }}
                    >
                        <CloseIcon />
                    </IconButton>
                </div>
            </StyledDialogTitle>

            <StyledDialogContent>
                <ImagesContainer imgCount={images.length} dialogType={dialogType}>
                    {images.map((src, index) => (
                        src &&
                        <ImageWrapper
                            key={index}
                            dialogType={dialogType}
                            imgCount={images.length}
                            style={{
                                width: getImageWidth(),
                                maxWidth: (dialogType === 'faceMatch' && images.length === 1) ? '550px' : getImageWidth()
                            }}
                        >
                            <TransformWrapper
                                initialScale={1}
                            >
                                {({ zoomIn, zoomOut, resetTransform }) => (
                                    <React.Fragment>
                                        <TransformComponent
                                            wrapperStyle={{
                                                width: '100%',
                                                height: '100%',
                                            }}
                                            contentStyle={{
                                                width: '100%',
                                                height: '100%',
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                overflow: 'hidden',
                                                borderRadius: '4px',
                                                position: 'relative',
                                                objectFit: dialogType === 'faceMatch' ? 'contain' : 'cover',
                                            }}
                                        >
                                            <StyledImage
                                                src={`data:image/jpeg;base64,${src}`}
                                                alt={`Image ${index + 1}`}
                                                imageType={getImageType()}
                                            />
                                        </TransformComponent>
                                        <ZoomControlsContainer>
                                            <IconButton
                                                onClick={() => zoomIn()}
                                                size="small"
                                                color="primary"
                                                sx={{ color: '#264E5A' }}
                                            >
                                                <ZoomInIcon />
                                            </IconButton>
                                            <IconButton
                                                onClick={() => zoomOut()}
                                                size="small"
                                                color="primary"
                                                sx={{ color: '#264E5A' }}
                                            >
                                                <ZoomOutIcon />
                                            </IconButton>
                                            <IconButton
                                                onClick={() => resetTransform()}
                                                size="small"
                                                color="primary"
                                                sx={{ color: '#264E5A' }}
                                            >
                                                <RestartAltIcon />
                                            </IconButton>
                                        </ZoomControlsContainer>
                                    </React.Fragment>
                                )}
                            </TransformWrapper>
                        </ImageWrapper>
                    ))}
                </ImagesContainer>
            </StyledDialogContent>
        </StyledDialog>

    )
}

export default ZoomDialog